import { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Stack,
  Button,
  IconButton,
  Grid,
  Tabs,
  Tab,
  Divider,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  User,
  Mail,
  Phone,
  Calendar,
  Briefcase,
  Award,
  Edit,
  ArrowLeft,
  MapPin,
  Shield,
  PhoneCall,
  Building2,
  UserCheck,
  Upload,
  FileText,
  Plus,
  Download,
  Trash2,
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useStaffProfile,
  useUpdateStaffStatus,
  useStaffDocuments,
  useCreateStaffDocument,
  useUpdateStaffDocument,
  useDeleteStaffDocument,
} from '@/hooks/useStaffManagement';
import { Loader } from '@/components/feedback/Loader';
import { StatusBadge } from '@/components/feedback/StatusBadge';
import { PageHeader } from '@/components/navigation/PageHeader';
import { EMPLOYMENT_STATUS_OPTIONS } from '@/constants/staff';
import { PATHS } from '@/router/paths';
import toast from 'react-hot-toast';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={500}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

export function StaffProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: profile, isLoading, error } = useStaffProfile(id);
  const { data: staffDocs } = useStaffDocuments(id);
  const statusMutation = useUpdateStaffStatus();
  const createDocMutation = useCreateStaffDocument();
  const updateDocMutation = useUpdateStaffDocument();
  const deleteDocMutation = useDeleteStaffDocument();

  const [tabValue, setTabValue] = useState(0);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);

  // Create document modal state
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [docType, setDocType] = useState('AADHAAR');
  const [docName, setDocName] = useState('');
  const [docFile, setDocFile] = useState(null);

  // Edit document modal state
  const [editDocDialogOpen, setEditDocDialogOpen] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [editDocType, setEditDocType] = useState('AADHAAR');
  const [editDocName, setEditDocName] = useState('');
  const [editDocFile, setEditDocFile] = useState(null);

  // Delete document modal state
  const [deleteDocDialogOpen, setDeleteDocDialogOpen] = useState(false);
  const [deleteDocId, setDeleteDocId] = useState(null);

  const handleOpenStatusMenu = (e) => {
    setStatusMenuAnchor(e.currentTarget);
  };

  const handleCloseStatusMenu = () => {
    setStatusMenuAnchor(null);
  };

  const handleSelectStatus = async (newStatus) => {
    try {
      await statusMutation.mutateAsync({ id, employmentStatus: newStatus });
      toast.success(`Employment status updated to ${newStatus}`);
    } catch (err) {
      toast.error(err.message || 'Failed to update status');
    } finally {
      handleCloseStatusMenu();
    }
  };

  const handleOpenDocDialog = () => {
    setDocType('AADHAAR');
    setDocName('');
    setDocFile(null);
    setDocDialogOpen(true);
  };

  const handleCloseDocDialog = () => {
    setDocDialogOpen(false);
  };

  const handleUploadDocument = async () => {
    if (!docFile) {
      toast.error('Please select a document file to upload');
      return;
    }
    try {
      await createDocMutation.mutateAsync({
        staffId: id,
        document_type: docType,
        document_name: docName || undefined,
        file: docFile,
      });
      toast.success('Staff document uploaded successfully');
      handleCloseDocDialog();
    } catch (err) {
      toast.error(err.message || 'Failed to upload staff document');
    }
  };

  const handleOpenEditDocDialog = (doc) => {
    setSelectedDocId(doc.id);
    setEditDocType(doc.document_type || 'AADHAAR');
    setEditDocName(doc.document_name || '');
    setEditDocFile(null);
    setEditDocDialogOpen(true);
  };

  const handleCloseEditDocDialog = () => {
    setEditDocDialogOpen(false);
    setSelectedDocId(null);
  };

  const handleUpdateDocument = async () => {
    if (!selectedDocId) return;
    try {
      const res = await updateDocMutation.mutateAsync({
        documentId: selectedDocId,
        staffId: id,
        document_type: editDocType,
        document_name: editDocName || undefined,
        file: editDocFile || undefined,
      });
      toast.success(res?.message || 'Staff document updated successfully.');
      handleCloseEditDocDialog();
    } catch (err) {
      toast.error(err.message || 'Failed to update staff document');
    }
  };

  const handleOpenDeleteDocDialog = (docId) => {
    setDeleteDocId(docId);
    setDeleteDocDialogOpen(true);
  };

  const handleCloseDeleteDocDialog = () => {
    setDeleteDocDialogOpen(false);
    setDeleteDocId(null);
  };

  const handleConfirmDeleteDocument = async () => {
    if (!deleteDocId) return;
    try {
      const res = await deleteDocMutation.mutateAsync({
        documentId: deleteDocId,
        staffId: id,
      });
      toast.success(res?.message || 'Staff document deleted successfully.');
      handleCloseDeleteDocDialog();
    } catch (err) {
      toast.error(err.message || 'Failed to delete staff document');
    }
  };

  if (isLoading) return <Loader />;

  if (error || !profile) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Staff member profile not found or failed to load.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowLeft size={18} />}
          onClick={() => navigate(PATHS.STAFF_MANAGEMENT_STAFF)}
          sx={{ mt: 2 }}
        >
          Back to Staff Directory
        </Button>
      </Box>
    );
  }

  const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Unnamed Staff';
  const departmentName = profile.department?.name || 'Unassigned Department';
  const designationName = profile.designation?.name || 'Unassigned Designation';
  const status = (profile.employment_status || profile.status || 'ACTIVE').toLowerCase();
  const documents = staffDocs || profile.documents || [];

  return (
    <Box>
      {/* Page Header with title & right action buttons */}
      <PageHeader
        title="Staff Profile"
        subtitle="View and manage staff member profile, employment status, and records"
        action={
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<ArrowLeft size={18} />}
              onClick={() => navigate(PATHS.STAFF_MANAGEMENT_STAFF)}
              sx={{ bgcolor: 'white' }}
            >
              Back to Directory
            </Button>

            <Button
              variant="outlined"
              startIcon={<UserCheck size={18} />}
              onClick={handleOpenStatusMenu}
              sx={{ bgcolor: 'white' }}
            >
              Change Status
            </Button>

            <Button
              variant="contained"
              startIcon={<Edit size={18} />}
              onClick={() => navigate(PATHS.STAFF_MANAGEMENT_STAFF_EDIT.replace(':id', id))}
              sx={{ fontWeight: 600 }}
            >
              Edit Staff Member
            </Button>
          </Stack>
        }
      />

      {/* Header Profile Card */}
      <Paper sx={{ p: { xs: 3, md: 4 }, mb: 4, borderRadius: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{ alignItems: { xs: 'center', md: 'flex-start' } }}
        >
          <Avatar
            src={profile.photo_url || ''}
            sx={{
              width: 100,
              height: 100,
              fontSize: 36,
              fontWeight: 700,
              bgcolor: 'primary.main',
            }}
          >
            {(profile.first_name || 'S').charAt(0)}
          </Avatar>

          <Box sx={{ flexGrow: 1, textAlign: { xs: 'center', md: 'left' } }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Box>
                <Typography variant="h4" fontWeight={700} gutterBottom>
                  {fullName}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-start' } }}
                >
                  {profile.employee_code && (
                    <Chip
                      label={profile.employee_code}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                  <Chip
                    label={departmentName}
                    size="small"
                    icon={<Building2 size={14} />}
                    sx={{ bgcolor: 'primary.50', color: 'primary.main', fontWeight: 500 }}
                  />
                  <Chip
                    label={designationName}
                    size="small"
                    icon={<Briefcase size={14} />}
                    sx={{ bgcolor: 'secondary.50', color: 'secondary.main', fontWeight: 500 }}
                  />
                  <StatusBadge status={status} />
                </Stack>
              </Box>
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              sx={{ mt: 2, color: 'text.secondary', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-start' } }}
            >
              {profile.email && (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Mail size={16} />
                  <Typography variant="body2">{profile.email}</Typography>
                </Stack>
              )}
              {profile.phone && (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Phone size={16} />
                  <Typography variant="body2">{profile.phone}</Typography>
                </Stack>
              )}
              {profile.address && (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <MapPin size={16} />
                  <Typography variant="body2">{profile.address}</Typography>
                </Stack>
              )}
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {/* Quick Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: 'primary.50',
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Building2 size={24} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Department
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {departmentName}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: 'info.50',
                color: 'info.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Briefcase size={24} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Designation
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {designationName}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: 'success.50',
                color: 'success.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Award size={24} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Experience
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {profile.experience_years !== undefined
                  ? `${profile.experience_years} Years`
                  : 'N/A'}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: 'warning.50',
                color: 'warning.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Calendar size={24} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Role
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {profile.role?.description || profile.role?.name || profile.user?.role?.description || profile.user?.role?.name || 'Staff Member'}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs Section */}
      <Paper sx={{ borderRadius: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label="Overview" />
          <Tab label="Employment & Qualification" />
          <Tab label="Emergency Contact" />
          <Tab label="Account Details" />
          <Tab label="Documents" />
        </Tabs>

        <Box sx={{ px: { xs: 2, md: 4 } }}>
          {/* Tab 0: Overview */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" mb={2} fontWeight={600}>
                  Personal Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <DetailRow label="First Name" value={profile.first_name} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <DetailRow label="Last Name" value={profile.last_name} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <DetailRow label="Gender" value={profile.gender} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <DetailRow label="Date of Birth" value={profile.date_of_birth} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <DetailRow label="Blood Group" value={profile.blood_group} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <DetailRow label="Employee Code" value={profile.employee_code} />
                  </Grid>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" mb={2} fontWeight={600}>
                  Contact Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <DetailRow label="Phone Number" value={profile.phone} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <DetailRow label="Email Address" value={profile.email} />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <DetailRow label="Residential Address" value={profile.address} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 1: Employment & Qualification */}
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" mb={2} fontWeight={600}>
                  Employment Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <DetailRow label="Employee Code" value={profile.employee_code} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <DetailRow label="Role" value={profile.role?.description || profile.role?.name || profile.user?.role?.description || profile.user?.role?.name || '-'} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <DetailRow label="Department" value={departmentName} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <DetailRow label="Designation" value={designationName} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <DetailRow label="Joining Date" value={profile.joining_date} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <DetailRow label="Employment Status" value={profile.employment_status || profile.status} />
                  </Grid>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" mb={2} fontWeight={600}>
                  Qualifications & Experience
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <DetailRow label="Academic / Professional Qualification" value={profile.qualification} />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <DetailRow
                      label="Total Experience"
                      value={profile.experience_years !== undefined ? `${profile.experience_years} Years` : '—'}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 2: Emergency Contact */}
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" mb={2} fontWeight={600}>
                  Emergency Contact Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <DetailRow label="Contact Person Name" value={profile.emergency_contact_name} />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <DetailRow label="Emergency Phone Number" value={profile.emergency_contact_phone} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 3: Account Details */}
          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" mb={2} fontWeight={600}>
                  System Account Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <DetailRow label="User ID" value={profile.account?.user_id || profile.id} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <DetailRow label="Account Status" value={profile.account?.status || profile.employment_status || 'ACTIVE'} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <DetailRow label="Last Login" value={profile.account?.last_login || 'Never logged in'} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 4: Documents */}
          <TabPanel value={tabValue} index={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Staff Documents
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Uploaded identity, qualification, and official records
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<Upload size={18} />}
                onClick={handleOpenDocDialog}
              >
                Upload Document
              </Button>
            </Box>

            {(!documents || documents.length === 0) ? (
              <Box
                sx={{
                  py: 6,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                }}
              >
                <FileText size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  No documents have been uploaded for this staff member yet.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Plus size={16} />}
                  onClick={handleOpenDocDialog}
                  sx={{ mt: 1 }}
                >
                  Add First Document
                </Button>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {documents.map((doc, idx) => {
                  const fileUrl = doc.file_url || doc.document_url;
                  const uploadedAt = doc.created_at || doc.uploaded_at;
                  return (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={doc.id || idx}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          height: '100%',
                        }}
                      >
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0, flexGrow: 1 }}>
                              <FileText size={20} color="#1976d2" style={{ flexShrink: 0 }} />
                              <Typography variant="subtitle1" fontWeight={700} noWrap>
                                {doc.document_name || doc.document_type || 'Document'}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0, ml: 1 }}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenEditDocDialog(doc)}
                                title="Edit Document"
                              >
                                <Edit size={16} />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleOpenDeleteDocDialog(doc.id)}
                                title="Delete Document"
                              >
                                <Trash2 size={16} />
                              </IconButton>
                            </Stack>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                            <Chip
                              label={doc.document_type || 'GENERAL'}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontWeight: 600, fontSize: 11 }}
                            />
                            {uploadedAt && (
                              <Typography variant="caption" color="text.secondary">
                                Uploaded: {new Date(uploadedAt).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {fileUrl && (
                          <Button
                            variant="contained"
                            size="small"
                            component="a"
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<Download size={14} />}
                            sx={{ mt: 2, alignSelf: 'flex-start' }}
                          >
                            View Document
                          </Button>
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </TabPanel>
        </Box>
      </Paper>

      {/* Status Selection Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={handleCloseStatusMenu}
      >
        {EMPLOYMENT_STATUS_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.value}
            selected={(profile.employment_status || profile.status || 'ACTIVE').toUpperCase() === opt.value}
            onClick={() => handleSelectStatus(opt.value)}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Upload Document Dialog */}
      <Dialog open={docDialogOpen} onClose={handleCloseDocDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Upload Staff Document</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            <TextField
              select
              label="Document Type *"
              fullWidth
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
            >
              <MenuItem value="AADHAAR">Aadhaar Card / ID Proof</MenuItem>
              <MenuItem value="PAN_CARD">PAN Card</MenuItem>
              <MenuItem value="QUALIFICATION_CERTIFICATE">Qualification Certificate</MenuItem>
              <MenuItem value="EXPERIENCE_LETTER">Experience Letter</MenuItem>
              <MenuItem value="JOINING_LETTER">Joining Letter</MenuItem>
              <MenuItem value="RESUME">Resume / CV</MenuItem>
              <MenuItem value="OTHER">Other Document</MenuItem>
            </TextField>

            <TextField
              label="Document Name / Description"
              placeholder="e.g. Master Degree Certificate / Aadhaar Front"
              fullWidth
              value={docName}
              onChange={(e) => setDocName(e.target.value)}
            />

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                SELECT FILE *
              </Typography>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<Upload size={18} />}
                sx={{ py: 1.5, borderStyle: 'dashed' }}
              >
                {docFile ? docFile.name : 'Choose File to Upload'}
                <input
                  type="file"
                  hidden
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setDocFile(e.target.files[0]);
                    }
                  }}
                />
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDocDialog} disabled={createDocMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUploadDocument}
            disabled={!docFile || createDocMutation.isPending}
            startIcon={createDocMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <Upload size={16} />}
          >
            {createDocMutation.isPending ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Document Dialog */}
      <Dialog open={editDocDialogOpen} onClose={handleCloseEditDocDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Update Staff Document</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 0.5 }}>
            <TextField
              select
              label="Document Type *"
              fullWidth
              value={editDocType}
              onChange={(e) => setEditDocType(e.target.value)}
            >
              <MenuItem value="AADHAAR">Aadhaar Card / ID Proof</MenuItem>
              <MenuItem value="PAN_CARD">PAN Card</MenuItem>
              <MenuItem value="QUALIFICATION_CERTIFICATE">Qualification Certificate</MenuItem>
              <MenuItem value="EXPERIENCE_LETTER">Experience Letter</MenuItem>
              <MenuItem value="JOINING_LETTER">Joining Letter</MenuItem>
              <MenuItem value="RESUME">Resume / CV</MenuItem>
              <MenuItem value="OTHER">Other Document</MenuItem>
            </TextField>

            <TextField
              label="Document Name / Description"
              placeholder="e.g. Master Degree Certificate / Aadhaar Front"
              fullWidth
              value={editDocName}
              onChange={(e) => setEditDocName(e.target.value)}
            />

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                REPLACE FILE (OPTIONAL)
              </Typography>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<Upload size={18} />}
                sx={{ py: 1.5, borderStyle: 'dashed' }}
              >
                {editDocFile ? editDocFile.name : 'Choose New File (Optional)'}
                <input
                  type="file"
                  hidden
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setEditDocFile(e.target.files[0]);
                    }
                  }}
                />
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseEditDocDialog} disabled={updateDocMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateDocument}
            disabled={updateDocMutation.isPending}
            startIcon={updateDocMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <Edit size={16} />}
          >
            {updateDocMutation.isPending ? 'Updating...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Document Confirmation Dialog */}
      <Dialog open={deleteDocDialogOpen} onClose={handleCloseDeleteDocDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>Delete Document</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            Are you sure you want to delete this staff document? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDeleteDocDialog} disabled={deleteDocMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteDocument}
            disabled={deleteDocMutation.isPending}
            startIcon={deleteDocMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <Trash2 size={16} />}
          >
            {deleteDocMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
